import { ComponentCountSummary, ComponentInstance, ComponentLifecycleEvent, ComponentTypeStats } from '../types';

import { BaseComponentLifecyclePlugin } from './BaseComponentLifecyclePlugin';
import Vue from 'vue';

interface LifecycleStore {
  componentStats: ComponentCountSummary;
  events: ComponentLifecycleEvent[];
}

export class StateManagerPlugin extends BaseComponentLifecyclePlugin {
  private store: LifecycleStore;
  private maxEvents: number;
  private componentMap: Map<string, ComponentInstance>;
  private componentStats: Map<string, ComponentTypeStats>;
  private creationRate: number = 0;
  private destructionRate: number = 0;
  private enabled: boolean = true;

  constructor(maxEvents: number = 50) {
    super('component-lifecycle-state-manager');
    this.maxEvents = maxEvents;
    this.componentMap = new Map<string, ComponentInstance>();
    this.componentStats = new Map<string, ComponentTypeStats>();

    // Make the store reactive using Vue.observable
    this.store = Vue.observable({
      componentStats: {
        totalComponents: 0,
        byType: [],
        creationRate: 0,
        destructionRate: 0
      },
      events: []
    });
  }

  getStore(): LifecycleStore {
    return this.store;
  }

  onComponentCreated(component: ComponentInstance): void {
    if (!this.enabled) return;

    // Add component to map
    this.componentMap.set(component.id, component);

    // Update stats
    this.updateComponentStats(component.name, component, true);

    // Create event
    const event: ComponentLifecycleEvent = {
      type: 'created',
      componentName: component.name,
      timestamp: Date.now(),
      componentId: component.id
    };

    // Add to events (limit to maxEvents)
    this.store.events.unshift(event);
    if (this.store.events.length > this.maxEvents) {
      this.store.events.pop();
    }

    // Update stats
    this.updateStoreStats();
  }

  onComponentDestroyed(component: ComponentInstance): void {
    if (!this.enabled) return;

    // Remove from map if exists
    this.componentMap.delete(component.id);

    // Update stats
    this.updateComponentStats(component.name, component, false);

    // Create event
    const event: ComponentLifecycleEvent = {
      type: 'destroyed',
      componentName: component.name,
      timestamp: Date.now(),
      componentId: component.id
    };

    // Add to events (limit to maxEvents)
    this.store.events.unshift(event);
    if (this.store.events.length > this.maxEvents) {
      this.store.events.pop();
    }

    // Update stats
    this.updateStoreStats();
  }

  onStatsUpdated(stats: ComponentCountSummary): void {
    if (!this.enabled) return;

    // Update creation and destruction rates
    this.creationRate = stats.creationRate;
    this.destructionRate = stats.destructionRate;

    // Update store stats
    this.updateStoreStats();
  }

  private updateComponentStats(name: string, component: ComponentInstance, isCreation: boolean): void {
    let stats = this.componentStats.get(name);

    if (!stats) {
      stats = {
        name,
        count: 0,
        instances: []
      };
      this.componentStats.set(name, stats);
    }

    if (isCreation) {
      stats.count++;
      stats.instances.push(component);
    } else {
      stats.count = Math.max(0, stats.count - 1);
      stats.instances = stats.instances.filter(c => c.id !== component.id);

      // Clean up empty component types
      if (stats.count === 0) {
        this.componentStats.delete(name);
      }
    }
  }

  private updateStoreStats(): void {
    // Update the store's component stats (make it reactive)
    const totalComponents = Array.from(this.componentStats.values())
      .reduce((sum, stat) => sum + stat.count, 0);

    this.store.componentStats = {
      totalComponents,
      byType: Array.from(this.componentStats.values()).sort((a, b) => b.count - a.count),
      creationRate: this.creationRate,
      destructionRate: this.destructionRate
    };
  }

  clearEvents(): void {
    this.store.events = [];
  }

  getComponentByName(name: string): ComponentInstance[] {
    return Array.from(this.componentMap.values())
      .filter(comp => comp.name === name);
  }

  getComponent(id: string): ComponentInstance | undefined {
    return this.componentMap.get(id);
  }
}
